import React, { useState } from "react";

import { usePrintPreview } from "@/hooks/usePrintPreview";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useProductionOrderStore } from "./store/useProductionOrderStore";

import { CalendarDays, User, Factory } from "lucide-react";
import {
  Printer,
  CheckCircle2,
  Play,
  Settings,
  Package,
  ClipboardList,
  Pause,
  Wrench,
  Loader2,
  Trash2,
  Save,
  X,
  Pencil,
  Monitor,
  Lock,
  Unlock,
  ScanBarcode,
  AlertTriangle,
  MoreHorizontal,
  Eye,
  Zap,
  ShieldCheck,
  FileText,
  Ban,
  Rocket,
  RotateCcw,
} from "lucide-react";
import { ShopfloorScannerModal } from "@/components/factory/ShopfloorScannerModal";
import { cn, formatNumber, cleanProcessText } from "@/lib/utils";
import { formatProductSpecs } from "@/lib/format-specs";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ProductionOrderStatus, ProductionPriority, getStatusLabel } from "@/config/status-registry";
import { calculateGangRunAreaWeights } from "@apv-erp/status";
// Separator removed — no longer used in tab layout
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import { trpcClient } from "@/lib/trpc";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useRoleAccess } from "@/hooks/useRoleAccess";

import AttachmentSection from "@/components/shared/AttachmentSection";
import {
  mapProductionOrderDetail,
  type ProductionOrderApi,
} from "@/lib/transformers/production-orders";
import { toast } from "sonner";
import type { PaginatedData } from "@/shared/schemas";
import WorkflowStepper from "@/components/shared/WorkflowStepper";
import { useWorkflowSteps } from "@/hooks/useWorkflowSteps";
import { ZIndexView, useZIndexView } from "@/components/shared/ZIndexView";
import EntityLink from "@/components/shared/EntityLink";
import ImageLightbox from "@/components/shared/ImageLightbox";
import { ProductionThumbnailCell } from "@/components/shared/ProductionThumbnailCell";
import { ReportFinishedGoodDialog } from "./components/ReportFinishedGoodDialog";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

import {
  History,
  MessageSquare,
  Paperclip,
  LayoutDashboard,
  Layers,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { POProcessTab } from "./tabs/POProcessTab";
import { POMaterialTab } from "./tabs/POMaterialTab";
import { POToolingTab } from "./tabs/POToolingTab";
import { POQualityTab } from "./tabs/POQualityTab";
import { ProductionOrderPrintModal } from "@/components/planning/print/ProductionOrderPrintModal";
import CartonLabelPrintModal from "@/components/quality/print/CartonLabelPrintModal";
import { QrCode } from "lucide-react";
import { POSVGMarquette } from "./components/POSVGMarquette";
import { QuickUploadLayoutImageDialog } from "./components/QuickUploadLayoutImageDialog";
import { DetailField as F } from "@/components/shared/DetailField";
import { POProcessWorkflowRibbon } from "./components/POProcessWorkflowRibbon";
import { POReadinessPanel } from "./components/POReadinessPanel";
import { RePrepressRequestModal } from "./components/RePrepressRequestModal";
import { ComponentGroupRow } from "./components/ComponentGroupRow";
import { isComponentTreeMode, orderComponentTree } from "./utils/component-tree";
import DetailNotFoundState from "@/components/shared/DetailNotFoundState";

// -- Material Planning Status helpers --
function getMPStatusColor(status: string): string {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return "bg-amber-100 text-amber-700 border-amber-200";
    case "ORDERED":
      return "bg-blue-100 text-blue-700 border-blue-200";
    case "PARTIAL_RECEIVED":
      return "bg-indigo-100 text-indigo-700 border-indigo-200";
    case "RECEIVED":
      return "bg-emerald-100 text-emerald-700 border-emerald-200";
    case "CANCELLED":
      return "bg-slate-100 text-slate-500 border-slate-200";
    default:
      return "bg-slate-100 text-slate-600 border-slate-200";
  }
}
function getMPStatusLabel(status: string): string {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return "Chờ cấp";
    case "ORDERED":
      return "Đã đặt";
    case "PARTIAL_RECEIVED":
      return "Nhận 1 phần";
    case "RECEIVED":
      return "Đã nhận đủ";
    case "CANCELLED":
      return "Hủy";
    default:
      return status || "—";
  }
}


export default function ProductionOrderDetailPage({
  poId: propId,
  isZIndex = false,
}: {
  poId?: string;
  isZIndex?: boolean;
} = {}) {
  const params = useParams({ strict: false }) as any;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isInitTreeRunning, setIsInitTreeRunning] = useState(false);
  const orderId = propId || params?.orderId;
  const VALID_TABS = ["products", "materials", "toolings", "operations", "finishing"] as const;
  type POTabKey = typeof VALID_TABS[number];

  const [activeTab, setActiveTab] = useState<string>(() => {
    if (typeof window !== "undefined") {
      const urlParams = new URLSearchParams(window.location.search);
      const tabFromUrl = urlParams.get("tab");
      if (tabFromUrl && (VALID_TABS as readonly string[]).includes(tabFromUrl)) {
        return tabFromUrl;
      }
    }
    return "products";
  });

  const handleTabChange = (newTab: string) => {
    setActiveTab(newTab);
    navigate({
      // @ts-expect-error TanStack Router strict mode không resolve search type cho routes dùng loose validateSearch
      search: (prev: Record<string, unknown>) => ({ ...prev, tab: newTab }),
      replace: true,
    });
  };

  const [highlightStepId, setHighlightStepId] = useState<string | null>(null);

  React.useEffect(() => {
    if (highlightStepId) {
      handleTabChange("operations");
    }
  }, [highlightStepId]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showStatusDialog, setShowStatusDialog] = useState(false);
  const [statusReason, setStatusReason] = useState("");
  const [pendingStatus, setPendingStatus] = useState<string | null>(null);
  const { workflow } = useWorkflowSteps("production");
  const zIndex = useZIndexView();
  const roleAccess = useRoleAccess();
  const storeData = useProductionOrderStore();

  const [reportFgDialog, setReportFgDialog] = useState<{
    open: boolean;
    orderDetailId: string;
    productName: string;
    plannedQty: number;
    producedQty: number;
  }>({
    open: false,
    orderDetailId: "",
    productName: "",
    plannedQty: 0,
    producedQty: 0,
  });

  const [showMrpDialog, setShowMrpDialog] = useState(false);
  const [mrpWarningData, setMrpWarningData] = useState<any[]>([]);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [cartonLabelOpen, setCartonLabelOpen] = useState(false);
  const [rePrepressOpen, setRePrepressOpen] = useState(false);
  const [quickUploadLayout, setQuickUploadLayout] = useState<any | null>(null);

  // Fetch production order from API
  const {
    data: orderData,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["production-order", orderId],
    queryFn: async () => {
      const raw = (await trpcClient.productionOrders.getById.query({
        id: orderId,
      })) as ProductionOrderApi;
      return mapProductionOrderDetail(raw);
    },
  });

  const lsx = orderData as any;

  // Fetch production logs
  const { data: logsData, isLoading: loadingLogs } = useQuery({
    queryKey: ["production-logs", orderId],
    queryFn: async () => {
      try {
        const data = (await trpcClient.machineLogs.list.query({
          productionOrderId: orderId,
          limit: 50,
        })) as PaginatedData<any>;
        return data?.items || [];
      } catch {
        return [];
      }
    },
  });

  // Fetch order details (products) + full salesOrder from linked sales order
  const { data: linkedOrderData } = useQuery({
    queryKey: ["order-full", orderData?.salesOrderId],
    queryFn: async () => {
      if (!orderData?.salesOrderId) return { items: [], salesOrder: null };
      try {
        const order = (await trpcClient.orders.getById.query({
          id: orderData.salesOrderId,
        })) as any;
        return {
          items: order?.items || order?.orderDetails || [],
          salesOrder: order,
        };
      } catch {
        return { items: [], salesOrder: null };
      }
    },
    enabled: !!orderData?.salesOrderId,
  });

  const orderLogs = logsData || [];
  const orderProducts = linkedOrderData?.items || [];
  const salesOrder = linkedOrderData?.salesOrder || lsx?.salesOrder;

  // Resolve display values
  // A-24: không lấy tên sản phẩm làm tên khách hàng — LSX không có khách hàng thì hiện "—"
  const customerName =
    salesOrder?.customer?.name ||
    salesOrder?.customer?.shortName ||
    lsx?.customer?.name ||
    lsx?.customer?.shortName ||
    "—";
  const customerId = salesOrder?.customerId || salesOrder?.customer?.id || lsx?.customerId || lsx?.customer?.id;
  const orderNumber =
    salesOrder?.orderId || salesOrder?.poNumber || lsx?.poReference || "—";
  const deadline = salesOrder?.deliveryDeadline || lsx?.deadline;
  const priorityLabel = Object.fromEntries(
    Object.entries(ProductionPriority).map(([k, v]) => [k, v.label])
  );
  const statusLabel = Object.fromEntries(
    Object.entries(ProductionOrderStatus).map(([k, v]) => [k, v.label])
  );


  // --- CRUD Handlers ---

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await trpcClient.productionOrders.delete.mutate({ id: orderId });

      navigate({ to: "/production-orders" });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi xóa");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusAction = async (newStatus: string) => {
    setPendingStatus(newStatus);
    setStatusReason("");
    setShowStatusDialog(true);
  };

  const handleUpdateStatus = async (newStatus: string, reason?: string) => {
    try {
      const res: any = await trpcClient.productionOrders.updateStatus.mutate({
        id: orderId,
        status: newStatus as any,
      });
      if (res?.warning) toast.warning(res.warning, { duration: 10000 });

      if (reason) {
        try {
          await trpcClient.comments.create.mutate({
            entityType: "production_order",
            entityId: orderId,
            content: `Lý do ${newStatus === "CANCELLED" ? "huỷ" : "tạm dừng"}: ${reason}`,
          });
        } catch (e) {
          console.error("Failed to add reason comment", e);
        }
      }

      await queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      await queryClient.invalidateQueries({
        queryKey: ["activity-log", "production-order", orderId],
      });
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi cập nhật trạng thái");
    }
  };

  const handlePublishPO = async (ignoreWarning: boolean = false) => {
    try {
      const result = (await trpcClient.productionOrders.publishPO.mutate({
        id: orderId,
        ignoreMrpWarning: ignoreWarning,
      })) as any;

      if (result && result.success === false && result.code === "MRP_WARNING") {
        setMrpWarningData(result.shortfalls || []);
        setShowMrpDialog(true);
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: ["production-order"],
      });
      toast.success("Đã ban hành lệnh sản xuất");
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi ban hành lệnh");
    }
  };

  const handleCreateDraftPRs = async () => {
    try {
      await trpcClient.productionOrders.createDraftPRs.mutate({
        id: orderId,
        shortfalls: mrpWarningData,
      });
      setShowMrpDialog(false);
      await handlePublishPO(true);
    } catch (err: any) {
      toast.error(err?.message || "Lỗi khi tạo Yêu cầu mua hàng");
    }
  };

  // --- Loading & Error States ---
  if (isLoading) {
    return (
      <StandardDetailLayout
        breadcrumbs={[{ label: "Lệnh sản xuất", to: "/production-orders" }]}
        title={<Skeleton className="h-8 w-48" />}
        entityId="Đang tải..."
        entityType="production_order"
        showContextPanel={false}
        isZIndex={isZIndex}
      >
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-x-4 gap-y-6">
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
            <div className="col-span-1 md:col-span-2 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
            <div className="col-span-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
            <div className="col-span-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
            <div className="col-span-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
            <div className="col-span-1 space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-5 w-full" />
            </div>
          </div>
        </div>

        <div className="mt-6">
          <Skeleton className="h-10 w-full max-w-4xl mb-4" />
          <Card className="shadow-sm border-slate-200">
            <CardContent className="p-6">
              <div className="space-y-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            </CardContent>
          </Card>
        </div>
      </StandardDetailLayout>
    );
  }

  if (error || !lsx) {
    return (
      <DetailNotFoundState
        breadcrumbs={[{ label: "Lệnh sản xuất", to: "/production-orders" }]}
        entityLabel="Lệnh sản xuất"
        backTo="/production-orders"
        codeOrId={orderId}
        error={error}
        onRetry={() => refetch()}
      />
    );
  }

  // --- Derived Data ---
  const routingSteps = lsx.routingSteps || lsx.routing || [];
  const totalSteps = routingSteps.length;
  const completedSteps = routingSteps.filter(
    (s: any) => s.status === "completed" || s.status === "COMPLETED",
  ).length;

  // Prefer progress from API if available, otherwise calculate
  const progressPercent =
    lsx.progress !== undefined
      ? lsx.progress
      : totalSteps > 0
        ? Math.round((completedSteps / totalSteps) * 100)
        : 0;

  const isUrgent = lsx.priority === "urgent" || lsx.priority === "URGENT";

  // Số khuôn & kẽm thật của lệnh = tổng SL các dòng (quan hệ cũ dieCuts/plates chỉ dùng khi chưa có dòng). Không dùng số giả.
  const lsxMoldRows = (lsx.toolings || []).filter((t: any) => t.toolingType && t.toolingType !== "PLATE" && t.toolingType !== "ZINC");
  const lsxPlateRows = (lsx.toolings || []).filter((t: any) => t.toolingType === "PLATE" || t.toolingType === "ZINC");
  const sumToolingQty = (rows: any[]) => rows.reduce((a: number, t: any) => a + Math.max(1, Number(t.quantity) || 1), 0);
  const moldCount = lsxMoldRows.length ? sumToolingQty(lsxMoldRows) : lsx.dieCuts?.length || 0;
  const plateCount = lsxPlateRows.length ? sumToolingQty(lsxPlateRows) : lsx.plates?.length || 0;
  const actualQtyPercent = (lsx.qtyOrder && lsx.qtyOrder > 0)
    ? Math.min(100, Math.round(((lsx.actualQuantity || 0) / lsx.qtyOrder) * 100))
    : 0;

  // Cây Sản phẩm → Hợp phần (tab 1): hợp phần nằm dưới dòng sản phẩm của nó; lệnh 1 hợp phần giữ bảng phẳng
  const lsxComponentTreeMode = isComponentTreeMode(lsx.components);
  const lsxComponentNodes = orderComponentTree(
    lsx.components,
    (lsx.productionOrderSources || []).map((s: any) => s.id)
  );
  const handleInitComponentTree = async () => {
    try {
      setIsInitTreeRunning(true);
      const r: any = await (trpcClient as any).productionOrders.initComponentTree.mutate({ id: lsx.id });
      toast.success(
        r?.created > 0
          ? `Đã thêm ${r.created} hợp phần từ sản phẩm`
          : "Các hợp phần của sản phẩm đã có đủ trong lệnh"
      );
      queryClient.invalidateQueries({ queryKey: ["production-order"] });
    } catch (err: any) {
      toast.error(err?.message || "Không khởi tạo được hợp phần");
    } finally {
      setIsInitTreeRunning(false);
    }
  };
  const renderComponentRows = (nodes: any[]) =>
    nodes.map((node) => {
      const c = node.component;
      const ts = c.techSpecs || {};
      const dims = [ts.length, ts.width, ts.height].filter((v: any) => Number(v) > 0).join("×");
      const colors = Number(ts.frontColors || 0) + Number(ts.backColors || 0) + Number(ts.spotColors || 0);
      const nSteps = (c.routingSteps || []).length;
      const nMaterials = (lsx.materialPlans || []).filter((m: any) => m.componentId === c.id).length;
      const nToolings = (lsx.toolings || []).filter((t: any) => t.componentId === c.id).length;
      return (
        <TableRow key={`comp-${c.id}`} data-testid="po-component-row" data-depth={node.depth + 1} className="bg-slate-50/40 hover:bg-slate-50">
          <TableCell />
          <TableCell colSpan={12} className="py-1.5">
            <div className="flex items-center gap-2 text-xs flex-wrap" style={{ paddingLeft: node.depth * 16 }}>
              <span className="text-indigo-300 font-mono">└</span>
              <span className="font-semibold text-slate-800">{node.name}</span>
              {dims && <span className="text-slate-500">{dims} mm</span>}
              {colors > 0 && <span className="text-slate-500">{colors} màu{ts.hasUV ? " + UV" : ""}</span>}
              <span className="ml-auto text-[11px] text-slate-500">
                {nSteps} công đoạn · {nMaterials} vật tư · {nToolings} khuôn/kẽm
              </span>
            </div>
          </TableCell>
        </TableRow>
      );
    });

  // Dòng đơn hàng của lệnh — dùng cho "Yêu cầu bình lại"
  const rePrepressItems: Array<{ id: string; productName: string; prepressCode?: string; quantity?: number; status?: string }> =
    (lsx.productionOrderSources || []).length > 0
      ? (lsx.productionOrderSources || [])
          .filter((s: any) => s.orderDetailId)
          .map((s: any) => ({
            id: s.orderDetailId,
            productName: s.orderDetail?.productName || s.orderDetail?.product?.productName || lsx.productName || "Sản phẩm",
            prepressCode: s.orderDetail?.prepressCode || lsx.layoutCode || undefined,
            quantity: s.quantity ?? undefined,
            status: s.orderDetail?.status,
          }))
      : lsx.orderDetailId
        ? [{ id: lsx.orderDetailId, productName: lsx.productName || "Sản phẩm", prepressCode: lsx.layoutCode || undefined, quantity: lsx.qtyOrder ?? undefined, status: lsx.orderDetail?.status }]
        : [];

  // Đơn hàng của lệnh (để đặt gia công một phần từ lệnh)
  const lsxOrderId: string | null =
    lsx.salesOrderId ||
    lsx.orderDetail?.orderId ||
    (lsx.productionOrderSources || []).map((s: any) => s.salesOrderId || s.orderDetail?.orderId).find(Boolean) ||
    null;

  // Gang Run / Unified products list for Tab 1
  const rawSources = (lsx.productionOrderSources && lsx.productionOrderSources.length > 0)
    ? lsx.productionOrderSources
    : (lsx.orderSources && lsx.orderSources.length > 0)
    ? lsx.orderSources
    : [];

  const sourceItems: any[] = rawSources.length > 0
    ? rawSources.map((s: any, idx: number) => {
        const qtyOrder = s.quantity || s.qtyOrder || 0;
        const qtyPlan = s.plannedQty || Math.ceil((s.quantity || 0) * 1.05);
        const qtyProduced = s.finishedQty ?? s.orderDetail?.producedQty ?? 0;
        const stockQty = s.inventoryQty ?? 0;
        const qtyDelivered = s.deliveredQty ?? s.orderDetail?.qtyDelivered ?? 0;
        const progressPercent = s.progressPercent ?? (qtyPlan > 0 ? Math.min(100, Math.round((qtyProduced / qtyPlan) * 100)) : 0);
        const status = s.orderDetail?.status || (qtyProduced >= qtyPlan && qtyPlan > 0 ? "COMPLETED" : "IN_PRODUCTION");

        const resolvedProd = s.orderDetail?.product || s.product || lsx.product;
        const resolvedPid = s.orderDetail?.productId || s.productId || lsx.productId || resolvedProd?.id;
        const resolvedPcode = s.orderDetail?.product?.productCode || s.product?.productCode || resolvedProd?.productCode || `SP-${idx + 1}`;
        const resolvedPname = s.orderDetail?.product?.productName || s.productName || resolvedProd?.productName || resolvedProd?.name || lsx.productName || `Mã hàng ${idx + 1}`;

        return {
          id: s.id,
          orderDetailId: s.orderDetailId,
          deliveryNotes: s.deliveryNotes || null,
          finishingNotes: s.finishingNotes || null,
          productId: resolvedPid,
          productCode: resolvedPcode,
          productName: resolvedPname,
          product: resolvedProd,
          specs: s.orderDetail?.specs || s.orderDetail?.product?.specs || s.specs || resolvedProd?.specs,
          process: s.orderDetail?.process || s.orderDetail?.product?.process || s.process || resolvedProd?.process,
          productProcesses: s.orderDetail?.product?.productProcesses || s.product?.productProcesses || resolvedProd?.productProcesses,
          qtyOrder,
          qtyPlan,
          qtyProduced,
          stockQty,
          qtyDelivered,
          ups: s.pcsPerSheet || s.ups || s.outs || 1,
          status,
          progressPercent,
          deliveryDeadline: s.orderDetail?.order?.deliveryDeadline || lsx.deadline,
          poRef: s.orderDetail?.order?.orderId || lsx.poReference,
        };
      })
    : orderProducts.length > 0
      ? orderProducts.map((item: any, idx: number) => {
          const qtyOrder = item.quantity || item.qtyOrder || 0;
          const qtyPlan = item.qtyPlan || item.product?.qtyPlan || lsx?.qtyPlan || qtyOrder;
          const qtyProduced = item.producedQty || item.completedQty || 0;
          const stockQty = item.stockQty ?? lsx.stockQty ?? 0;
          const qtyDelivered = item.qtyDelivered ?? lsx.deliveredQty ?? 0;
          const progressPercent = qtyPlan > 0 ? Math.min(100, Math.round((qtyProduced / qtyPlan) * 100)) : 0;
          const status = item.status || (qtyProduced >= qtyPlan && qtyPlan > 0 ? "COMPLETED" : "IN_PRODUCTION");

          const resolvedProd = item.product || lsx.product;
          const resolvedPid = item.productId || item.product?.id || lsx.productId || resolvedProd?.id;
          const resolvedPcode = item.productCode || item.product?.productCode || resolvedProd?.productCode || `SP-${idx + 1}`;
          const resolvedPname = item.productName || item.product?.name || item.product?.productName || resolvedProd?.productName || resolvedProd?.name || lsx.productName || "—";

          return {
            id: item.id || item.orderDetailId,
            orderDetailId: item.id || item.orderDetailId,
            productId: resolvedPid,
            productCode: resolvedPcode,
            productName: resolvedPname,
            product: resolvedProd,
            specs: item.specs || item.product?.specs || resolvedProd?.specs,
            process: item.process || item.product?.process || resolvedProd?.process,
            productProcesses: item.product?.productProcesses || resolvedProd?.productProcesses,
            qtyOrder,
            qtyPlan,
            qtyProduced,
            stockQty,
            qtyDelivered,
            ups: lsx.ups || lsx.prepress?.prepressPcsPerSheet || 1,
            status,
            progressPercent,
            deliveryDeadline: item.deliveryDeadline || lsx.deadline,
            poRef: orderNumber,
          };
        })
      : [
          {
            id: lsx.id,
            orderDetailId: lsx.orderDetailId,
            productId: lsx.productId,
            productCode: lsx.product?.productCode || lsx.orderId,
            productName: lsx.productName || lsx.product?.productName || "Sản phẩm chính",
            product: lsx.product,
            specs: lsx.specs || lsx.product?.specs,
            process: lsx.process || lsx.product?.process,
            productProcesses: lsx.product?.productProcesses,
            qtyOrder: lsx.qtyOrder || 0,
            qtyPlan: lsx.qtyPlan || 0,
            qtyProduced: lsx.actualQuantity || 0,
            stockQty: lsx.stockQty || 0,
            qtyDelivered: lsx.deliveredQty || 0,
            ups: lsx.ups || lsx.prepress?.prepressPcsPerSheet || 1,
            status: lsx.status || "DRAFT",
            progressPercent: lsx.qtyPlan > 0 ? Math.min(100, Math.round(((lsx.actualQuantity || 0) / lsx.qtyPlan) * 100)) : 0,
            deliveryDeadline: lsx.deadline,
            poRef: lsx.poReference || orderNumber,
          },
        ];

  const poRoutingSteps = (lsx.routingSteps || lsx.routing || []).map((step: any) => ({
    name: step.name || step.processName || step.stepId,
    status: step.status,
  }));

  return (
    <StandardDetailLayout
        tabTitle={lsx.lsxId || orderId}
      breadcrumbs={[{ label: "Lệnh sản xuất", to: "/production-orders" }]}
      title={
        <div className="flex items-center gap-2.5">
          <ProductionThumbnailCell
            imageUrl={lsx.product?.image || null}
            layoutUrl={lsx.printLayouts?.[0]?.fileUrl || null}
            title={lsx.lsxId || orderId}
            type="auto"
            size={32}
            className="rounded-md border border-slate-300 shadow-2xs bg-white"
          />
          <span className="font-bold text-slate-900 tracking-tight">{lsx.lsxId || orderId}</span>
        </div>
      }
      entityId={lsx.id}
      entityType="production_order"
      isZIndex={isZIndex}
      timestamps={{
        created: lsx.createdAt,
        updated: lsx.updatedAt,
        createdBy: lsx.createdBy || null,
        updatedBy: lsx.updatedBy || null,
      }}
      status={
        <div className="flex gap-1.5 items-center flex-wrap">
          <Badge
            variant="outline"
            data-testid="po-status-badge"
            className={cn(
              "text-xs font-semibold",
              ProductionOrderStatus[lsx.status]?.color || "bg-slate-100 text-slate-700 border-slate-200",
            )}
          >
            {ProductionOrderStatus[lsx.status]?.label || lsx.status}
          </Badge>
          {isUrgent && <Badge variant="destructive" className="text-[10px] px-1.5 py-0">GẤP</Badge>}
          {lsx.isPlanLocked && (
            <Badge
              variant="outline"
              className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0 font-medium"
            >
              <Lock className="mr-1 h-2.5 w-2.5" /> Đã chốt KH
            </Badge>
          )}
        </div>
      }
      actions={
        <div className="flex items-center gap-1.5 shrink-0 no-print">
          {/* Nút hành động Workflow chính */}
          {["IN_PROGRESS"].includes(lsx.status) && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs font-semibold shadow-xs"
              onClick={() => handleStatusAction("COMPLETED")}
            >
              <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" /> Hoàn thành
            </Button>
          )}
          {["DRAFT", "PENDING_APPROVAL", "PLANNED", "PRE_PRESS", "PRE_PRESS_COMPLETED", "READY_FOR_SCHEDULING"].includes(lsx.status) && (
            <Button
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 text-xs font-semibold shadow-xs whitespace-nowrap gap-1.5"
              onClick={() => handlePublishPO()}
            >
              <Rocket className="h-3.5 w-3.5" /> Duyệt Ban Hành
            </Button>
          )}
          {["RELEASED"].includes(lsx.status) && (
            <Button
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs font-semibold shadow-xs whitespace-nowrap gap-1.5"
              onClick={() => handleStatusAction("IN_PROGRESS")}
            >
              <Play className="h-3.5 w-3.5" /> Bắt đầu
            </Button>
          )}
          {["PAUSED", "ON_HOLD"].includes(lsx.status) && (
            <Button
              size="sm"
              className="bg-amber-600 hover:bg-amber-700 h-8 text-xs font-semibold shadow-xs whitespace-nowrap"
              onClick={() => handleStatusAction("IN_PROGRESS")}
            >
              <Play className="mr-1.5 h-3.5 w-3.5" /> Tiếp tục
            </Button>
          )}

          {/* Yêu cầu bình lại (chỉ khi chưa in — máy chủ kiểm tra công đoạn / nhật ký; đã in thì lập NCR / lệnh bù) */}
          {!["CANCELLED", "COMPLETED", "CLOSED", "MANUFACTURED"].includes(lsx.status) && rePrepressItems.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              className="h-8 text-xs font-medium bg-white text-rose-700 border-rose-300 hover:bg-rose-50 shadow-2xs whitespace-nowrap"
              onClick={() => setRePrepressOpen(true)}
              disabled={rePrepressItems.every((it) => it.status === "RE_PREPRESS")}
              title={
                rePrepressItems.every((it) => it.status === "RE_PREPRESS")
                  ? "Đang chờ chế bản bình lại"
                  : "Yêu cầu chế bản bình lại bài in của lệnh này"
              }
            >
              <RotateCcw className="mr-1.5 h-3.5 w-3.5" /> Bình lại
            </Button>
          )}

          {/* In LSX */}
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs font-medium bg-white text-slate-700 border-slate-200 hover:bg-slate-50 shadow-2xs whitespace-nowrap"
            onClick={() => setPrintModalOpen(true)}
          >
            <Printer className="mr-1.5 h-3.5 w-3.5" /> In LSX
          </Button>

          {/* Sửa LSX */}
          <Button
            size="sm"
            variant="default"
            className="h-8 text-xs font-medium shadow-2xs whitespace-nowrap"
            onClick={() =>
              navigate({
                to: "/production-orders/$orderId/edit",
                params: { orderId: lsx.orderId || lsx.id || orderId },
              })
            }
            disabled={lsx.isPlanLocked}
          >
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Sửa
          </Button>

          {/* Dropdown menu gom nhóm các thao tác phụ: Quét sản lượng, Sửa nhanh, Khóa/Mở khóa, Tạm dừng, Hủy, Tái đồng bộ, Xóa */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs font-medium bg-white text-slate-700 border-slate-200 hover:bg-slate-50 shadow-2xs gap-1 px-2.5"
              >
                <span>Thao tác</span>
                <MoreHorizontal className="h-3.5 w-3.5 text-slate-500" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuItem onClick={() => setScannerOpen(true)} className="cursor-pointer text-xs">
                <ScanBarcode className="mr-2 h-4 w-4 text-indigo-600" /> Quét sản lượng
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => setCartonLabelOpen(true)}
                className="cursor-pointer text-xs"
                data-testid="po-print-carton-labels"
              >
                <QrCode className="mr-2 h-4 w-4 text-emerald-600" /> In tem thùng
              </DropdownMenuItem>
              {/* Gia công một phần: từ lệnh chọn công đoạn gửi ra ngoài (lệnh phải thuộc đơn hàng) */}
              {lsxOrderId && !["COMPLETED", "CANCELLED"].includes(lsx.status) && (
                <DropdownMenuItem
                  className="cursor-pointer text-xs"
                  data-testid="po-create-outsource"
                  onClick={() =>
                    navigate({
                      to: "/outsource-orders/create",
                      search: {
                        orderId: lsxOrderId,
                        productionOrderId: lsx.id,
                        lsxCode: lsx.orderId,
                        model: "HYBRID_FINISH_EXTERNAL",
                        ...(rePrepressItems.length ? { detailIds: rePrepressItems.map((d) => d.id).join(",") } : {}),
                      } as any,
                    })
                  }
                >
                  <Factory className="mr-2 h-4 w-4 text-orange-600" /> Đặt gia công (một phần)
                </DropdownMenuItem>
              )}

              {["IN_PROGRESS"].includes(lsx.status) && (
                <DropdownMenuItem onClick={() => handleStatusAction("PAUSED")} className="cursor-pointer text-xs text-amber-700">
                  <Pause className="mr-2 h-4 w-4 text-amber-600" /> Tạm dừng lệnh
                </DropdownMenuItem>
              )}

              {lsx.isPlanLocked ? (
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await trpcClient.productionPlanning.unlockPlan.mutate({
                        id: lsx?.id || orderId,
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ["production-order"],
                      });
                      toast.success("Đã mở khóa kế hoạch");
                    } catch (err: any) {
                      toast.error(err.message);
                    }
                  }}
                  className="cursor-pointer text-xs text-amber-700"
                >
                  <Unlock className="mr-2 h-4 w-4 text-amber-600" /> Mở khóa KH
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem
                  onClick={async () => {
                    try {
                      await trpcClient.productionPlanning.lockPlan.mutate({
                        id: lsx?.id || orderId,
                      });
                      await queryClient.invalidateQueries({
                        queryKey: ["production-order"],
                      });
                      toast.success("Đã chốt kế hoạch (Khoá)");
                    } catch (err: any) {
                      toast.error(err.message);
                    }
                  }}
                  className="cursor-pointer text-xs text-indigo-700"
                >
                  <Lock className="mr-2 h-4 w-4 text-indigo-600" /> Chốt kế hoạch
                </DropdownMenuItem>
              )}

              {["DRAFT", "PENDING_APPROVAL"].includes(lsx.status) && !lsx.isPlanLocked && (
                <DropdownMenuItem
                  onClick={async () => {
                    if (confirm("Thao tác này sẽ XÓA BỎ các chỉnh sửa thủ công trên Kế hoạch vật tư / Sản xuất và thiết lập lại theo đúng Báo Giá gốc. Bạn có chắc chắn?")) {
                      toast.loading("Đang đồng bộ lại...", { id: 'resync' });
                      try {
                        await trpcClient.productionOrders.reSyncFromEstimate.mutate({ productionOrderId: lsx.id });
                        toast.success("Đồng bộ Kế hoạch thành công!", { id: 'resync' });
                        queryClient.invalidateQueries({ queryKey: ["production-order"] });
                      } catch (e: any) {
                        toast.error("Lỗi đồng bộ: " + e.message, { id: 'resync' });
                      }
                    }
                  }}
                  className="cursor-pointer text-xs text-indigo-600"
                >
                  <RefreshCw className="mr-2 h-4 w-4 text-indigo-500" /> Tái đồng bộ Báo Giá
                </DropdownMenuItem>
              )}

              {!["COMPLETED", "CLOSED", "CANCELLED"].includes(lsx.status) && (
                <DropdownMenuItem
                  onClick={() => handleStatusAction("CANCELLED")}
                  className="cursor-pointer text-xs text-rose-700 focus:text-rose-800 focus:bg-rose-50"
                >
                  <Ban className="mr-2 h-4 w-4 text-rose-600" /> Hủy lệnh
                </DropdownMenuItem>
              )}

              <DropdownMenuSeparator />

              <DropdownMenuItem
                onClick={() => setShowDeleteConfirm(true)}
                disabled={lsx.isPlanLocked}
                className="cursor-pointer text-xs text-rose-600 focus:text-rose-700 focus:bg-rose-50"
              >
                <Trash2 className="mr-2 h-4 w-4 text-rose-500" /> Xóa lệnh sản xuất
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      }
      workflow={
        <WorkflowStepper
          workflow={workflow}
          currentStatus={lsx.status}
          onStatusChange={handleStatusAction}
        />
      }
    >
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Xác nhận xóa lệnh sản xuất?"
        description="Hành động này không thể hoàn tác. Toàn bộ kế hoạch và công đoạn liên quan sẽ bị xóa theo."
        onConfirm={handleDelete}
        isLoading={isDeleting}
        variant="destructive"
      />
      {/* ── BANNER CẢNH BÁO KẾ HOẠCH NỘI BỘ (CHƯA BAN HÀNH) ── */}
      {["DRAFT", "PENDING_APPROVAL", "PRE_PRESS", "PLANNED", "READY_FOR_SCHEDULING"].includes(lsx.status) && (
        <div className="bg-amber-50/90 border border-amber-200 rounded-lg px-3.5 py-2.5 flex items-center justify-between gap-3 text-xs mb-3 shadow-2xs text-amber-900">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0" />
            <span>
              <strong>Kế Hoạch Nội Bộ:</strong> Lệnh sản xuất này chưa được duyệt ban hành. Các công đoạn sản xuất, cấp phát giấy và khuôn kẽm chỉ hiển thị tại phân xưởng sau khi được duyệt ban hành.
            </span>
          </div>
          <Button
            size="sm"
            onClick={() => handlePublishPO()}
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 px-3 text-xs font-semibold shrink-0 gap-1.5 shadow-2xs cursor-pointer"
          >
            <Rocket className="h-3.5 w-3.5" />
            Duyệt ban hành
          </Button>
        </div>
      )}

      {/* ── THÔNG TIN CHUNG: 3 CARD LUÔN HIỆN (như trang khách hàng, không thu gọn) ── */}
      {(
        <div className="relative mb-5">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-stretch">
            {/* Card 1: Vector Marquette Bài Bình (3 cols) */}
            <div className="md:col-span-3">
              <POSVGMarquette
                sheetWidth={lsx.printingPaperSize?.split('x')?.[0] || 42.5}
                sheetHeight={lsx.printingPaperSize?.split('x')?.[1] || 67}
                ups={lsx.ups || lsx.prepress?.prepressPcsPerSheet || 24}
                layoutCode={lsx.layoutCode || lsx.orderId || "Bình bài"}
                imageUrl={lsx.printLayouts?.[0]?.fileUrl || lsx.product?.image || null}
                printLayouts={lsx.printLayouts || []}
                onUploadImage={(targetLayout) => {
                  const selected = targetLayout || lsx.printLayouts?.[0] || {
                    id: lsx.id,
                    layoutName: lsx.orderId || "Bài bình 1",
                    fileUrl: lsx.printLayouts?.[0]?.fileUrl || lsx.product?.image || null,
                  };
                  setQuickUploadLayout(selected);
                }}
              />
            </div>

            {/* Card 2: Tiến độ & Đơn hàng (5 cols) */}
            <Card className="md:col-span-5 shadow-sm border-slate-200 flex flex-col justify-between">
              <CardHeader className="py-2.5 px-4 bg-slate-50/60 border-b flex flex-row items-center justify-between">
                <CardTitle className="text-xs font-bold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                  <ClipboardList className="h-4 w-4 text-indigo-600" /> Tiến độ &amp; Đơn hàng
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-3 flex-1 flex flex-col justify-between">
                <div className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs">
                  <div className="min-w-0 overflow-hidden">
                    <span className="text-slate-400 block text-[10px] uppercase font-semibold">Khách hàng</span>
                    <EntityLink
                      entityType="customer"
                      id={customerId}
                      label={customerName}
                      className="font-bold text-slate-800 block text-xs break-words line-clamp-2"
                      onClick={isZIndex && customerId ? (e) => { e.preventDefault(); zIndex.openView("customer", customerId); } : undefined}
                    />
                  </div>
                  <div className="min-w-0 overflow-hidden">
                    <span className="text-slate-400 block text-[10px] uppercase font-semibold">Đơn hàng / PO</span>
                    <EntityLink
                      entityType="order"
                      id={salesOrder?.id}
                      code={orderNumber}
                      label={lsx.poReference ? `(${lsx.poReference})` : undefined}
                      className="font-mono font-bold text-indigo-700 block text-xs break-words"
                      onClick={isZIndex && salesOrder?.id ? (e) => { e.preventDefault(); zIndex.openView("order", salesOrder.id); } : undefined}
                    />
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px] uppercase font-semibold">Hạn giao &amp; KHSX</span>
                    <div className="flex flex-col">
                      <span className="font-semibold text-rose-600 font-mono">
                        Hạn: {deadline ? new Date(deadline).toLocaleDateString("vi-VN") : "—"}
                      </span>
                      {Boolean(lsx.scheduleStartDate || lsx.scheduleEndDate || (lsx.routingSteps && lsx.routingSteps.length > 0)) && (
                        <span className="text-[10px] text-indigo-700 font-mono font-medium">
                          KHSX: {(() => {
                            const steps = lsx.routingSteps || [];
                            const start = lsx.scheduleStartDate || steps[0]?.scheduledStartTime || steps[0]?.plannedStartDate;
                            const end = lsx.scheduleEndDate || steps[steps.length - 1]?.scheduledEndTime || steps[steps.length - 1]?.plannedEndDate;
                            if (!start && !end) return "Chưa xếp lịch";
                            const sFmt = start ? new Date(start).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }) : "";
                            const eFmt = end ? new Date(end).toLocaleDateString("vi-VN", { day: "2-digit", month: "2-digit" }) : "";
                            return sFmt && eFmt ? `${sFmt} → ${eFmt}` : eFmt ? `Đến ${eFmt}` : "—";
                          })()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div>
                    <span className="text-slate-400 block text-[10px] uppercase font-semibold">Kế hoạch SL</span>
                    <div className="font-bold text-slate-800 text-xs leading-snug">
                      YC: {formatNumber(lsx.qtyOrder || 0)} <span className="text-slate-400 font-normal">pcs</span>
                      <span className="text-amber-700 ml-1.5 font-semibold">+{formatNumber(lsx.qtyAllowance || 0)} bù</span>
                    </div>
                    <div className="text-[10px] text-slate-500 font-mono mt-0.5">
                      Tổng KH: <strong className="text-indigo-700">{formatNumber(lsx.qtyPlan || 0)}</strong> pcs ({lsx.ups || 1} UPS • {formatNumber(lsx.runSheetsNeeded || lsx.netSheets || Math.ceil((lsx.qtyPlan || 1) / (lsx.ups || 1)))} tờ)
                    </div>
                  </div>
                </div>

                {/* Tiến độ kép */}
                <div className="pt-2 border-t border-slate-100 space-y-2">
                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-slate-500 font-medium">Tiến độ công đoạn</span>
                      <span className="font-bold text-slate-700">{completedSteps}/{totalSteps} bước ({progressPercent}%)</span>
                    </div>
                    <Progress value={progressPercent} className="h-1.5" />
                  </div>
                  <div>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-slate-500 font-medium">Sản lượng hoàn thành</span>
                      <span className="font-bold text-emerald-700 font-mono">
                        {formatNumber(lsx.actualQuantity || 0)} / {formatNumber(lsx.qtyOrder || 0)} pcs ({actualQtyPercent}%)
                      </span>
                    </div>
                    <Progress value={actualQtyPercent} className="h-1.5 bg-emerald-100 [&>div]:bg-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Card 3: Sẵn sàng triển khai (4 cols) — bấm ô mở tab tương ứng */}
            <POReadinessPanel productionOrder={lsx} sourceItems={sourceItems} onNavigateTab={handleTabChange} />
          </div>
        </div>
      )}

      {/* ── WORKFLOW QUY TRÌNH SẢN XUẤT THƯỜNG TRỰC (Thông tin chung - Rule 3.1 & Phase 227) ── */}
      <POProcessWorkflowRibbon
        productionOrder={lsx}
        routingSteps={routingSteps}
        activeTab={activeTab}
        onNavigateTab={handleTabChange}
        onSelectStep={(stepId) => {
          setHighlightStepId(stepId);
          handleTabChange("operations");
        }}
        highlightStepId={highlightStepId}
      />

      {/* ── CANONICAL 5 TABS INTERFACE (Rule 2.1 & Rule 3 Parity) ── */}
      <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
        {/* ── STICKY TABLINE BAR (Rule 3 & Rule 4 Invariants) ── */}
        <div className="sticky top-[45px] z-20 -mx-4 sm:-mx-6 px-4 sm:px-6 bg-white/95 backdrop-blur-md border-y border-slate-200 shadow-2xs mb-5">
          <TabsList>
            <TabsTrigger
              value="products"
              data-testid="po-view-tab-products"
              className="gap-1.5"
            >
              <Layers className="h-4 w-4 text-indigo-600 shrink-0" />
              <span>1. Danh mục sản phẩm</span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono ml-0.5">
                {sourceItems.length}
              </Badge>
            </TabsTrigger>

            <TabsTrigger
              value="materials"
              data-testid="po-view-tab-materials"
              className="gap-1.5"
            >
              <Package className="h-4 w-4 text-indigo-600 shrink-0" />
              <span>2. Kế hoạch vật tư</span>
              <Badge
                variant="outline"
                className={cn(
                  "text-[10px] px-1.5 py-0 font-medium ml-0.5",
                  lsx.materialStatus === "READY"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : lsx.materialStatus === "PARTIAL"
                    ? "bg-amber-50 text-amber-700 border-amber-200"
                    : "bg-slate-100 text-slate-600 border-slate-200"
                )}
              >
                {lsx.materialStatus === "READY" ? "Đủ" : lsx.materialStatus === "PARTIAL" ? "1 phần" : "Chờ cấp"}
              </Badge>
            </TabsTrigger>

            <TabsTrigger
              value="toolings"
              data-testid="po-view-tab-toolings"
              className="gap-1.5"
            >
              <Wrench className="h-4 w-4 text-emerald-600 shrink-0" />
              <span>3. Kế hoạch khuôn kẽm</span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono ml-0.5">
                {moldCount} khuôn • {plateCount}{" "}
                kẽm
              </Badge>
            </TabsTrigger>

            <TabsTrigger
              value="operations"
              data-testid="po-view-tab-operations"
              className="gap-1.5"
            >
              <Factory className="h-4 w-4 text-indigo-600 shrink-0" />
              <span>4. Kế hoạch sản xuất</span>
              <Badge variant="secondary" className="text-[10px] px-1.5 py-0 font-mono ml-0.5">
                {completedSteps}/{totalSteps} bước ({progressPercent}%)
              </Badge>
            </TabsTrigger>

            <TabsTrigger
              value="finishing"
              data-testid="po-view-tab-finishing"
              className="gap-1.5"
            >
              <ShieldCheck className="h-4 w-4 text-indigo-600 shrink-0" />
              <span>5. KCS &amp; Đóng gói</span>
              {lsx.approvalLogs && lsx.approvalLogs.length > 0 && (
                <Badge variant="outline" className="text-[10px] px-1.5 py-0 font-mono ml-0.5 bg-emerald-50 text-emerald-700 border-emerald-200">
                  {lsx.approvalLogs.length} ký
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>
        </div>

        {/* ═══ TAB 1: DANH MỤC SẢN PHẨM ═══ */}
        <TabsContent value="products" data-testid="po-view-tabcontent-products" className="space-y-4 focus-visible:outline-none">
          {(lsx.notes || lsx.techSpecs?.notes) && (
            <Card className="border-amber-200 bg-amber-50/50 shadow-sm">
              <CardContent className="p-3.5 flex items-start gap-3">
                <div className="p-1.5 rounded-md bg-amber-100 text-amber-800 shrink-0 mt-0.5">
                  <FileText className="h-4 w-4" />
                </div>
                <div className="space-y-1 min-w-0 flex-1">
                  <h4 className="text-xs font-bold text-amber-900 uppercase tracking-wide">
                    Chỉ đạo &amp; Ghi chú sản xuất
                  </h4>
                  <p className="text-xs text-amber-950 font-medium whitespace-pre-line leading-relaxed">
                    {lsx.notes || lsx.techSpecs?.notes}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          <div data-testid="po-component-toolbar" className="flex flex-wrap items-center justify-between gap-2 text-xs text-slate-600">
            <span>
              Cấu trúc hợp phần:{" "}
              <strong className="text-slate-800">{lsxComponentNodes.length} hợp phần</strong>
              {lsxComponentTreeMode ? " (hiển thị theo cây trong các tab)" : ""}
            </span>
            {(lsx.productionOrderSources || []).length > 0 && (
              <Button
                size="sm"
                variant="outline"
                className="h-8 text-xs border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                onClick={handleInitComponentTree}
                disabled={isInitTreeRunning || Boolean(lsx.isPlanLocked && !roleAccess.isAdmin)}
                title="Thêm các hợp phần khai báo ở sản phẩm gốc (chỉ thêm phần còn thiếu)"
              >
                {isInitTreeRunning ? "Đang khởi tạo..." : "Khởi tạo hợp phần từ sản phẩm"}
              </Button>
            )}
          </div>

          <Card className="shadow-sm border-slate-200">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50/80">
                    <TableHead className="w-[45px] text-center">#</TableHead>
                    <TableHead className="min-w-[260px]">Sản phẩm &amp; Đơn hàng</TableHead>
                    <TableHead className="min-w-[130px] text-center" title="Mã bài bình, khổ in mộc và số con trên tờ">Bài bình • Khổ in</TableHead>
                    <TableHead className="text-right w-[90px]" title="Số lượng khách đặt hàng theo PO/Hợp đồng">SL Đặt</TableHead>
                    <TableHead className="text-right w-[90px] text-blue-700 bg-blue-50/30 font-semibold" title="Tồn kho thành phẩm khả dụng">SL Tồn Kho</TableHead>
                    <TableHead className="text-right w-[95px] text-indigo-700 bg-indigo-50/30 font-semibold" title="Số lượng kế hoạch sản xuất phân bổ (đã gồm bù hao)">SL Kế Hoạch</TableHead>
                    <TableHead className="text-right w-[95px] text-amber-700 bg-amber-50/30 font-semibold" title="Số lượng máy đã chạy thực tế">SL Đã Chạy</TableHead>
                    <TableHead className="text-right w-[95px] text-emerald-700 bg-emerald-50/30 font-semibold" title="Số lượng thành phẩm thực tế đã hoàn thành KCS">SL Thành Phẩm</TableHead>
                    <TableHead className="text-right w-[90px] text-purple-700 font-semibold" title="Số lượng đã xuất kho giao cho khách hàng">SL Giao Hàng</TableHead>
                    <TableHead style={{ width: "210px" }} className="min-w-[180px]">Chi tiết</TableHead>
                    <TableHead className="text-center w-[100px]">Trạng Thái</TableHead>
                    <TableHead className="text-center w-[110px]" title="Tiến độ hoàn thành: (SL Thành phẩm / SL Kế hoạch) * 100">Tiến Độ</TableHead>
                    <TableHead className="text-center w-[50px]">Thao tác</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(() => {
                    const sourceAreaWeights = calculateGangRunAreaWeights(
                      sourceItems.map((it: any, i: number) => ({
                        itemId: String(it.orderDetailId || it.id || i),
                        ups: Number(it.ups || lsx.ups || 1),
                        flatLengthMm: Number(
                          it.specs?.flatLength ||
                            it.product?.flatLength ||
                            it.product?.specs?.flatLength ||
                            it.product?.length ||
                            100
                        ),
                        flatWidthMm: Number(
                          it.specs?.flatWidth ||
                            it.product?.flatWidth ||
                            it.product?.specs?.flatWidth ||
                            it.product?.width ||
                            100
                        ),
                      })),
                    );
                    return sourceItems.map((item, idx) => {
                      const itemQtyOrder = item.qtyOrder || 0;
                      const itemStockQty = item.stockQty || 0;
                      const itemQtyPlan = item.qtyPlan || itemQtyOrder;
                      const itemQtyProduced = item.qtyProduced || 0;
                      const itemQtyDelivered = item.qtyDelivered || 0;
                      const completionPercent = item.progressPercent ?? (itemQtyPlan > 0
                        ? Math.min(100, Math.round((itemQtyProduced / itemQtyPlan) * 100))
                        : 0);
                      const areaWeightPercent =
                        sourceAreaWeights[idx]?.weightPercent ??
                        lsx.techSpecs?.costAllocations?.[idx]?.weightPercent ??
                        100;

                      const specsText = formatProductSpecs(
                        item.specs ?? item.product?.specs,
                        item.product,
                      );
                      const productProcessesStr =
                        item.productProcesses?.map((p: any) => p.process?.name || "").filter(Boolean).join(", ") ||
                        item.product?.productProcesses?.map((p: any) => p.process?.name || "").filter(Boolean).join(", ") ||
                        poRoutingSteps.map((s: any) => s.name).filter(Boolean).join(", ");
                      const rawProcess = String(item.process || item.product?.process || productProcessesStr || "");
                      const processText = cleanProcessText(rawProcess, specsText);

                      return (
                        <React.Fragment key={'item-' + idx}>
                        <TableRow className="hover:bg-slate-50/80">
                          <TableCell className="text-slate-400 text-xs text-center font-mono font-bold">
                            {idx + 1}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-start gap-3">
                              {item.product?.image ? (
                                <ImageLightbox src={item.product.image} alt={item.productName || "HÌNH ẢNH SP"}>
                                  <img
                                    src={item.product.image}
                                    className="w-12 h-12 object-contain rounded border border-slate-200 shrink-0 cursor-pointer hover:opacity-80 transition-opacity bg-white"
                                    alt="HÌNH ẢNH SP"
                                  />
                                </ImageLightbox>
                              ) : (
                                <div className="w-12 h-12 rounded border border-slate-200 bg-slate-50 flex items-center justify-center shrink-0">
                                  <Package className="w-5 h-5 text-slate-300" />
                                </div>
                              )}
                              <div className="flex flex-col gap-0.5 min-w-0 flex-1">
                                <EntityLink
                                  entityType="product"
                                  id={item.productId}
                                  code={item.productCode}
                                  label={item.productName}
                                  className="font-semibold text-slate-900 text-sm"
                                  search={{ returnUrl: `/production-orders/${lsx.id || orderId}` }}
                                />

                                <div className="flex items-center gap-1.5 text-[11px] text-slate-500 font-mono mt-0.5">
                                  {item.poRef && <span>PO: {item.poRef}</span>}
                                  {salesOrder?.orderNumber && (
                                    <span>• ĐH: {salesOrder.orderNumber}</span>
                                  )}
                                </div>
                                {(item.deliveryNotes || item.finishingNotes) && (
                                  <div data-testid="po-source-notes" className="mt-1 space-y-0.5 text-[11px] text-slate-600">
                                    {item.deliveryNotes && <div><span className="font-semibold text-slate-700">Giao hàng:</span> {item.deliveryNotes}</div>}
                                    {item.finishingNotes && <div><span className="font-semibold text-slate-700">Thành phẩm:</span> {item.finishingNotes}</div>}
                                  </div>
                                )}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="font-mono text-xs font-bold text-slate-800">
                              {lsx.layoutCode || lsx.orderId || "Bài bình 1"}
                            </div>
                            <div className="text-[10px] text-slate-500 font-mono">
                              {lsx.printingPaperSize || lsx.sheetSize || "Khổ in chuẩn"} • <strong className="text-emerald-700">{item.ups || lsx.ups || 1} UPS</strong>
                            </div>
                            <div className="mt-1 flex items-center justify-center">
                              <span
                                data-testid={`po-view-area-weight-${idx}`}
                                className="px-1.5 py-0.5 rounded text-[10px] font-mono font-bold bg-purple-50 text-purple-700 border border-purple-200"
                                title="Tỷ trọng phân bổ giá thành giấy & công in theo diện tích chiếm chỗ (UPS × Dài × Rộng)"
                              >
                                % Giá thành: {areaWeightPercent}%
                              </span>
                            </div>
                          </TableCell>
                        <TableCell className="text-right font-mono font-bold text-slate-800">
                          {formatNumber(itemQtyOrder)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-blue-700 bg-blue-50/20">
                          {formatNumber(itemStockQty)}
                        </TableCell>
                        <TableCell className="text-right font-mono text-indigo-700 font-bold bg-indigo-50/20">
                          {formatNumber(itemQtyPlan)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-amber-700 bg-amber-50/20">
                          {formatNumber(itemQtyProduced)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-bold text-emerald-700 bg-emerald-50/20">
                          {formatNumber(itemQtyProduced)}
                        </TableCell>
                        <TableCell className="text-right font-mono font-semibold text-purple-700">
                          {formatNumber(itemQtyDelivered)}
                        </TableCell>
                        <TableCell className="align-top border-l border-slate-100">
                          {processText ? (
                            <div className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed">
                              {processText}
                            </div>
                          ) : (
                            <span className="text-slate-300">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-[10px] font-semibold px-2 py-0.5",
                              item.status === "COMPLETED" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                              item.status === "IN_PRODUCTION" ? "bg-blue-50 text-blue-700 border-blue-200" :
                              "bg-slate-50 text-slate-700 border-slate-200"
                            )}
                          >
                            {item.status ? getStatusLabel("RoutingStepStatus", item.status) : "Chờ SX"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center gap-1.5 justify-center">
                            <Progress value={completionPercent} className="h-1.5 w-12" />
                            <span className="text-[11px] font-bold text-slate-700 font-mono">
                              {completionPercent}%
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" className="h-7 w-7 p-0">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() =>
                                  salesOrder?.id ? zIndex.openView("order", salesOrder.id) : null
                                }
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                Xem Đơn hàng
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  if (item.productId) {
                                    navigate({
                                      to: "/products/$productId",
                                      params: { productId: item.productId },
                                      search: { returnUrl: `/production-orders/${lsx.id || orderId}`, tab: undefined },
                                    });
                                  }
                                }}
                              >
                                <Package className="mr-2 h-4 w-4" />
                                Xem Sản phẩm
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                onClick={() =>
                                  setReportFgDialog({
                                    open: true,
                                    orderDetailId: item.orderDetailId || item.id,
                                    productName: item.productName,
                                    plannedQty: itemQtyPlan,
                                    producedQty: itemQtyProduced,
                                  })
                                }
                              >
                                <ClipboardList className="mr-2 h-4 w-4 text-indigo-600" />
                                Báo cáo TP
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                      {lsxComponentTreeMode &&
                        renderComponentRows(lsxComponentNodes.filter((n) => n.sourceId && n.sourceId === item.id))}
                      </React.Fragment>
                    );
                  }).concat(
                    lsxComponentTreeMode
                      ? (() => {
                          const shared = lsxComponentNodes.filter(
                            (n) => !n.sourceId || !sourceItems.some((it: any) => it.id === n.sourceId)
                          );
                          return shared.length
                            ? [
                                <ComponentGroupRow key="comp-shared" node={null} colSpan={13} count={shared.length} unitLabel="hợp phần" />,
                                ...renderComponentRows(shared),
                              ]
                            : [];
                        })()
                      : []
                  );
                  })()}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

        </TabsContent>

        {/* ═══ TAB 2: KẾ HOẠCH VẬT TƯ ═══ */}
        <TabsContent value="materials" data-testid="po-view-tabcontent-materials" className="space-y-5 focus-visible:outline-none">
          <POMaterialTab
            orderId={lsx.id}
            components={lsx.components || []}
            materialPlans={lsx.materialPlans || []}
            isPlanLocked={Boolean(lsx.isPlanLocked && !roleAccess.isAdmin && !roleAccess.hasPermission("production-orders", "approve") && !roleAccess.hasPermission("production-orders", "update"))}
          />
        </TabsContent>

        {/* ═══ TAB 3: KẾ HOẠCH KHUÔN KẼM ═══ */}
        <TabsContent value="toolings" data-testid="po-view-tabcontent-toolings" className="space-y-6 focus-visible:outline-none">
          <POToolingTab
            orderId={lsx.id}
            toolings={lsx.toolings || []}
            processes={lsx.routingSteps || lsx.routing || []}
            dieCuts={lsx.dieCuts || []}
            plates={lsx.plates || []}
            isPlanLocked={Boolean(lsx.isPlanLocked && !roleAccess.isAdmin && !roleAccess.hasPermission("production-orders", "approve") && !roleAccess.hasPermission("production-orders", "update"))}
            productionOrder={lsx}
          />

        </TabsContent>

        {/* ═══ TAB 4: KẾ HOẠCH SẢN XUẤT ═══ */}
        <TabsContent value="operations" data-testid="po-view-tabcontent-operations" className="space-y-5 focus-visible:outline-none">
          <POProcessTab
            orderId={lsx.id}
            components={lsx.components || []}
            routingSteps={lsx.routingSteps || lsx.routing || []}
            qtyOrder={lsx.qtyOrder}
            qtyPlan={lsx.qtyPlan}
            qtyAllowance={lsx.qtyAllowance}
            ups={lsx.ups || 1}
            runSheetsNeeded={lsx.runSheetsNeeded}
            productionOrder={lsx}
            isPlanLocked={Boolean(lsx.isPlanLocked && !roleAccess.isAdmin && !roleAccess.hasPermission("production-orders", "approve") && !roleAccess.hasPermission("production-orders", "update"))}
            highlightStepId={highlightStepId}
            onNavigateTab={(tab) => handleTabChange(tab)}
          />

        </TabsContent>

        {/* ═══ TAB 5: NHẬT KÝ ĐÓNG GÓI ═══ */}
        <TabsContent value="finishing" data-testid="po-view-tabcontent-finishing" className="space-y-6 focus-visible:outline-none">
          {/* KCS của lệnh: IPQC / FQC / NCR (chuyển từ tab Kế hoạch sản xuất) */}
          <POQualityTab
            productionOrderId={lsx.id}
            order={lsx}
            isPlanLocked={Boolean(lsx.isPlanLocked && !roleAccess.isAdmin && !roleAccess.hasPermission("production-orders", "approve") && !roleAccess.hasPermission("production-orders", "update"))}
          />

          {/* Lịch sử Ký duyệt Kế hoạch */}
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-800">
                Lịch sử ký duyệt
              </h3>
            </div>
            {lsx.approvalLogs && lsx.approvalLogs.length > 0 ? (
              <div className="space-y-4">
                {lsx.approvalLogs.map((log: any, idx: number) => (
                  <div key={log.id} className="flex gap-4 relative">
                    {idx !== lsx.approvalLogs.length - 1 && (
                      <div className="absolute top-6 bottom-[-16px] left-[11px] w-[2px] bg-slate-100" />
                    )}
                    <div className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-slate-100 ring-4 ring-white">
                      {log.status === "APPROVED" ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />
                      ) : (
                        <Settings className="h-3.5 w-3.5 text-slate-500" />
                      )}
                    </div>
                    <div className="flex-1 pb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-slate-800">
                          {log.user?.fullName || log.user?.email || "Hệ thống"}
                        </span>
                        <span className="text-xs text-slate-500">
                          {new Date(log.createdAt).toLocaleString("vi-VN")}
                        </span>
                      </div>
                      <div className="text-sm text-slate-600 mt-0.5">
                        {log.action}{" "}
                        <Badge
                          variant="outline"
                          className={cn(
                            "text-[10px] ml-1",
                            log.status === "APPROVED"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-slate-50 text-slate-700"
                          )}
                        >
                          {log.status}
                        </Badge>
                      </div>
                      {log.notes && (
                        <div className="mt-1 text-xs text-slate-500 bg-slate-50 p-2 rounded italic">
                          "{log.notes}"
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-slate-500 italic">
                Chưa có lịch sử ký duyệt
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      <ZIndexView {...zIndex} />

      <Dialog open={showStatusDialog} onOpenChange={setShowStatusDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              Xác nhận{" "}
              {pendingStatus === "CANCELLED"
                ? "hủy"
                : pendingStatus === "PAUSED" || pendingStatus === "ON_HOLD"
                  ? (lsx.status === "PAUSED" || lsx.status === "ON_HOLD" ? "tiếp tục" : "tạm dừng")
                  : pendingStatus === "RELEASED"
                    ? "phát hành"
                    : pendingStatus === "COMPLETED"
                      ? "hoàn thành"
                      : pendingStatus === "IN_PROGRESS"
                        ? (lsx.status === "PAUSED" || lsx.status === "ON_HOLD" ? "tiếp tục sản xuất" : "bắt đầu sản xuất")
                        : "chuyển trạng thái"}{" "}
              lệnh sản xuất
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Vui lòng nhập lý do/ghi chú{" "}
              {["CANCELLED", "PAUSED", "ON_HOLD"].includes(pendingStatus || "")
                ? "(Bắt buộc)"
                : "(Tùy chọn)"}
            </label>
            <textarea
              className="w-full border border-slate-300 rounded-md p-2 text-sm focus:ring-indigo-500 focus:border-indigo-500 min-h-[100px]"
              placeholder={
                ["CANCELLED", "PAUSED", "ON_HOLD"].includes(pendingStatus || "")
                  ? "Vui lòng nhập chi tiết nguyên nhân..."
                  : "Nhập ghi chú (tùy chọn)..."
              }
              value={statusReason}
              onChange={(e) => setStatusReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowStatusDialog(false)}
            >
              Quay lại
            </Button>
            <Button
              variant={
                pendingStatus === "CANCELLED" ? "destructive" : "default"
              }
              className={
                pendingStatus === "CANCELLED"
                  ? "bg-rose-600 hover:bg-rose-700 text-white"
                  : "bg-indigo-600 hover:bg-indigo-700 text-white"
              }
              disabled={
                ["CANCELLED", "PAUSED", "ON_HOLD"].includes(pendingStatus || "") &&
                !statusReason.trim()
              }
              onClick={async () => {
                setShowStatusDialog(false);
                if (pendingStatus) {
                  await handleUpdateStatus(pendingStatus, statusReason);
                }
              }}
            >
              Xác nhận
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showMrpDialog} onOpenChange={setShowMrpDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Cảnh báo thiếu vật tư
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="mb-4 text-sm text-slate-600">
              Lệnh sản xuất này cần sử dụng các vật tư sau, nhưng tồn kho khả
              dụng không đủ. Bạn có muốn tự động tạo Yêu cầu mua hàng (PR) cho
              các vật tư bị thiếu không?
            </p>
            <div className="bg-slate-50 rounded-md border p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 border-b">
                  <tr>
                    <th className="px-4 py-2 text-left font-medium text-slate-600">
                      Vật tư
                    </th>
                    <th className="px-4 py-2 text-right font-medium text-slate-600">
                      Nhu cầu
                    </th>
                    <th className="px-4 py-2 text-right font-medium text-slate-600">
                      Khả dụng
                    </th>
                    <th className="px-4 py-2 text-right font-medium text-slate-600">
                      Cần mua
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {mrpWarningData.map((sf, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2 font-medium">
                        {sf.materialName}
                      </td>
                      <td className="px-4 py-2 text-right">{sf.qtyRequired}</td>
                      <td className="px-4 py-2 text-right text-red-600 font-medium">
                        {sf.qtyAvailable}
                      </td>
                      <td className="px-4 py-2 text-right text-amber-600 font-bold">
                        {sf.qtyToOrder}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMrpDialog(false)}>
              Hủy
            </Button>
            <Button
              variant="secondary"
              onClick={() => {
                setShowMrpDialog(false);
                handlePublishPO(true);
              }}
            >
              Bỏ qua & Ban hành
            </Button>
            <Button
              className="bg-amber-600 hover:bg-amber-700 text-white"
              onClick={handleCreateDraftPRs}
            >
              Tạo PR & Ban hành
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ShopfloorScannerModal
        initialCode={lsx?.lsxId || lsx?.code || orderId}
        open={scannerOpen}
        onOpenChange={setScannerOpen}
      />

      {reportFgDialog.open && (
        <ReportFinishedGoodDialog
          open={reportFgDialog.open}
          onOpenChange={(open) =>
            setReportFgDialog((prev) => ({ ...prev, open }))
          }
          orderDetailId={reportFgDialog.orderDetailId}
          productionOrderId={orderId}
          productName={reportFgDialog.productName}
          plannedQty={reportFgDialog.plannedQty}
          producedQty={reportFgDialog.producedQty}
        />
      )}

      <RePrepressRequestModal
        open={rePrepressOpen}
        onOpenChange={setRePrepressOpen}
        items={rePrepressItems.filter((it) => it.status !== "RE_PREPRESS")}
        onSuccess={() => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ["production-order"] });
          queryClient.invalidateQueries({ queryKey: ["activity-log", "production-order", orderId] });
        }}
      />

      <CartonLabelPrintModal
        open={cartonLabelOpen}
        productionOrderIds={[lsx?.id || orderId]}
        onClose={() => setCartonLabelOpen(false)}
      />

      {printModalOpen && (
        <ProductionOrderPrintModal
          orderIds={[orderId]}
          overrideData={lsx}
          onClose={() => setPrintModalOpen(false)}
        />
      )}

      <QuickUploadLayoutImageDialog
        open={!!quickUploadLayout}
        onOpenChange={(open) => !open && setQuickUploadLayout(null)}
        layout={quickUploadLayout}
        onSuccess={() => {
          refetch();
          queryClient.invalidateQueries({ queryKey: ["production-order"] });
        }}
      />
    </StandardDetailLayout>
  );
}
